// frontend/src/modules/projects/components/InvoiceTimeDialog.tsx
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { formatDate, formatMoney } from '../../../design-system/format';
import { projectsApi } from '../api';
import type { BillableTimeEntry } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  projectId: string;
  currency: string;
}

export function InvoiceTimeDialog({ open, onOpenChange, projectId, currency }: Props) {
  const navigate = useNavigate();
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const [groupBy, setGroupBy] = React.useState<'task' | 'project'>('task');
  const [entries, setEntries] = React.useState<BillableTimeEntry[]>([]);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const rows = await projectsApi.billableTime({ projectId, from: from || undefined, to: to || undefined });
      setEntries(rows);
      setSelected(new Set(rows.map((r) => r.id)));
    } catch (err: any) {
      toast.error('Could not load billable time', { description: err.message });
    } finally { setLoading(false); }
  }, [projectId, from, to]);

  React.useEffect(() => {
    if (open) { setFrom(''); setTo(''); setGroupBy('task'); setEntries([]); setSelected(new Set()); load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const chosen = entries.filter((e) => selected.has(e.id));
  const totalHours = Math.round(chosen.reduce((s, e) => s + e.hours, 0) * 100) / 100;
  const totalAmount = Math.round(chosen.reduce((s, e) => s + e.amount, 0) * 100) / 100;

  async function create() {
    if (chosen.length === 0) { toast.error('Select at least one entry'); return; }
    setBusy(true);
    try {
      const result = await projectsApi.invoiceTime({
        project_id: projectId,
        group_by: groupBy,
        entry_ids: Array.from(selected),
        from: from || undefined,
        to: to || undefined,
      });
      toast.success(`Created invoice ${result.invoice.invoice_number}`, {
        description: `${result.entry_count} entries · ${result.total_hours}h`,
      });
      onOpenChange(false);
      navigate(`/dashboard/invoices/${result.invoice.id}`);
    } catch (err: any) {
      toast.error('Invoice failed', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Invoice tracked time</DialogTitle>
          <DialogDescription>Turn billable, un-invoiced hours into a draft invoice.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 pt-2">
          <div className="grid grid-cols-3 gap-4">
            <div className="grid gap-1.5">
              <Label>From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} onBlur={load} />
            </div>
            <div className="grid gap-1.5">
              <Label>To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} onBlur={load} />
            </div>
            <div className="grid gap-1.5">
              <Label>Group by</Label>
              <Select value={groupBy} onValueChange={(v) => setGroupBy(v as 'task' | 'project')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="task">Task</SelectItem>
                  <SelectItem value="project">Project</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-xl ring-1 ring-border/50 overflow-hidden max-h-72 overflow-y-auto">
            {loading ? (
              <div className="py-8 px-5 text-[13px] text-muted-foreground">Loading billable time…</div>
            ) : entries.length === 0 ? (
              <div className="py-8 px-5 text-[13px] text-muted-foreground text-center">
                No billable, un-invoiced time in this range.
              </div>
            ) : (
              <ul className="divide-y divide-border/40">
                {entries.map((e) => (
                  <li key={e.id} className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
                    <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggle(e.id)}
                      className="h-4 w-4 rounded border-border accent-saffron shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{e.task_title ?? e.project_name}</p>
                      <p className="text-[11.5px] text-muted-foreground">{formatDate(e.start_at)} · {e.hours}h @ {formatMoney(e.effective_rate, currency)}</p>
                    </div>
                    <span className="tabular-nums font-semibold text-foreground">{formatMoney(e.amount, currency)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-between text-[13px]">
            <span className="text-muted-foreground">{chosen.length} selected · {totalHours}h</span>
            <span className="font-semibold text-foreground tabular-nums">{formatMoney(totalAmount, currency)}</span>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="button" disabled={busy || chosen.length === 0} onClick={create}>
              {busy ? 'Creating…' : 'Create invoice'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
